// Trade Account (BigCommerce registered vs guest) — badge, Customer 360 card,
// and status actions. Registered = ≥1 logged-in order (NumberOfRegOrders > 0).
import { useState } from "react";
import { BadgeCheck, UserX, Send, UserPlus, Store } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const TONE = {
  registered:    "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  needs_signup:  "bg-rose-500/15 text-rose-300 border-rose-500/40",
  invite_sent:   "bg-amber-500/15 text-amber-300 border-amber-500/40",
  created_by_us: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  no_orders:     "bg-slate-500/15 text-slate-400 border-slate-500/30",
};
const ICON = { registered: BadgeCheck, needs_signup: UserX, invite_sent: Send, created_by_us: UserPlus, no_orders: Store };

export function TradeAccountBadge({ ta, testid, compact = false }) {
  if (!ta) return <span className="text-slate-600 text-xs">—</span>;
  const Icon = ICON[ta.status] || Store;
  return (
    <span className={`ta-badge ta-${ta.status} inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${TONE[ta.status]}`}
      title={`${ta.label} · ${ta.reg_orders}/${ta.total_orders} orders logged in`} data-testid={testid}>
      <Icon className="w-3 h-3" />{compact && ta.status === "needs_signup" ? "Sign-up" : ta.label}
    </span>
  );
}

export async function setTradeStatus(personId, status, note) {
  const { data } = await api.patch(`/crm/persons/${personId}/trade-account`, { status, note });
  toast.success(`Trade Account → ${data.trade_account.label}`);
  return data.trade_account;
}

const ACTIONS = [
  { status: "invite_sent", label: "Mark invite sent", icon: Send },
  { status: "created_by_us", label: "Created by us", icon: UserPlus },
  { status: "needs_signup", label: "Reset to needs sign-up", icon: UserX },
];

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-2">
      <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className="text-sm font-semibold text-slate-100 tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}

export function TradeAccountCard({ personId, ta: initial, onChanged }) {
  const [ta, setTa] = useState(initial);
  const [busy, setBusy] = useState(false);
  if (!ta) return null;
  const act = async (status) => {
    setBusy(true);
    try { const next = await setTradeStatus(personId, status); setTa(next); onChanged?.(next); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update Trade Account"); }
    finally { setBusy(false); }
  };
  const gbp = (v) => `£${Number(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
  const manual = !["registered", "no_orders"].includes(ta.status);
  return (
    <div className="ta-card rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="trade-account-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Store className="w-4 h-4 text-orange-400" />
          <span className="text-sm font-semibold text-white">Trade Account</span>
        </div>
        <TradeAccountBadge ta={ta} testid="trade-account-status" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Total orders" value={ta.total_orders} testid="ta-total-orders" />
        <Stat label="Registered" value={ta.reg_orders} testid="ta-reg-orders" />
        <Stat label="Guest" value={ta.guest_orders} testid="ta-guest-orders" />
        <Stat label="Total value" value={gbp(ta.total_value)} testid="ta-total-value" />
      </div>
      {ta.total_orders > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-slate-400 font-mono mb-1">
            <span>Placed logged in</span><span data-testid="ta-pct">{ta.pct_logged_in ?? 0}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-emerald-400" style={{ width: `${ta.pct_logged_in ?? 0}%` }} />
          </div>
        </div>
      )}
      {manual && (
        <>
          <p className="text-[11px] text-slate-400 mt-3">No logged-in orders, so this customer can't see trade / tier discounts. Get them set up with an account.</p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {ACTIONS.filter((a) => a.status !== ta.status).map((a) => (
              <button key={a.status} type="button" disabled={busy} onClick={() => act(a.status)} data-testid={`ta-action-${a.status}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium border border-white/10 text-slate-300 hover:text-white hover:border-orange-400/50 disabled:opacity-50 transition-colors">
                <a.icon className="w-3 h-3" /> {a.label}
              </button>
            ))}
          </div>
        </>
      )}
      {ta.updated_by && <div className="text-[10px] text-slate-500 mt-2 font-mono" data-testid="ta-updated">Last change by {ta.updated_by} · {new Date(ta.updated_at).toLocaleDateString("en-GB")}</div>}
    </div>
  );
}
